import React, { useEffect, useState } from "react";
import { fetchServiceAndTaxSettings, saveServiceAndTaxSettingsAndSessions } from "../../../../services/Settings/ServicesandTaxSettingsAndSessios/ServicesAndTaxApi";
import { createManualDailySession, checkActiveDailySession, getActiveDailySessionData } from "../../../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";

import { Box, Typography, CircularProgress, Paper, FormControl, InputLabel, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { useFeatures } from "../../../../context/PermissionsAndFeatures/FeaturesContext";

const SessionSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // الحقول المطلوبة
  const [session, setSession] = useState("");
  // باقي الحقول مخفية لكن تُرسل مع الحفظ
  const [shift, setShift] = useState("");
  const [tax, setTax] = useState("");
  const [service, setService] = useState("");
  const [delivery, setDelivery] = useState("");
  const [taxIsPercentage, setTaxIsPercentage] = useState(true);
  const [serviceIsPercentage, setServiceIsPercentage] = useState(true);
  const [deliveryIsPercentage, setDeliveryIsPercentage] = useState(false);
  const [isServiceRequired, setIsServiceRequired] = useState(true);
  const [isDeliveryRequired, setIsDeliveryRequired] = useState(false);
  const [isTaxRequired, setIsTaxRequired] = useState(true);
  const [ctrlPPrint, setCtrlPPrint] = useState(false);
  const [autoPreparationPrint, setAutoPreparationPrint] = useState(false);
  const [invoiceIncrement, setInvoiceIncrement] = useState(""); // اجعلها فارغة افتراضياً

  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [changeDateDialogOpen, setChangeDateDialogOpen] = useState(false);
  const [newSessionDate, setNewSessionDate] = useState("");
  const [changeDateConfirmOpen, setChangeDateConfirmOpen] = useState(false);
  const [changeDateError, setChangeDateError] = useState("");
  const [manualSessionDate, setManualSessionDate] = useState("");

  // ميزات
  const { hasFeature, featuresLoading } = useFeatures();
  const dailyShiftSettingsEnabled = !featuresLoading && hasFeature("daily_shift_settings");

  const loadSettings = () => {
    setLoading(true);
    fetchServiceAndTaxSettings()
      .then((data) => {
        setSettings(data);
        setSession(data.session || "");
        setShift(data.shift || "");
        setTax(data.tax ?? "");
        setService(data.service ?? "");
        setDelivery(data.delivery ?? "");
        setTaxIsPercentage(Boolean(data.tax_is_percentage));
        setServiceIsPercentage(Boolean(data.service_is_percentage));
        setDeliveryIsPercentage(Boolean(data.delivery_is_percentage));
        setIsServiceRequired(Boolean(data.is_service_required));
        setIsDeliveryRequired(Boolean(data.is_delivery_is_required || data.is_delivery_required));
        setIsTaxRequired(Boolean(data.is_tax_required));
        setCtrlPPrint(Boolean(data.ctrl_p_print));
        setAutoPreparationPrint(Boolean(data.auto_preparation_print));
        setInvoiceIncrement(data.invoice_incremant || ""); // اجلبها كما هي من البيانات
      })
      .catch(() => setSettings(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    // جلب تاريخ اليومية الحالي من الدالة getActiveDailySessionData
    const fetchManualSessionDate = async () => {
      try {
        const result = await getActiveDailySessionData();
        // ابحث عن session_date في البيانات
        if (result && result.session_date) {
          setManualSessionDate(result.session_date);
        } else if (result && result.data && result.data.session_date) {
          setManualSessionDate(result.data.session_date);
        } else {
          setManualSessionDate("");
        }
      } catch {
        setManualSessionDate("");
      }
    };

    fetchManualSessionDate();
  }, [settings]);

  // أثناء تحميل حالة الميزات: عرض سبينر بسيط
  if (featuresLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSave = async () => {
    // منع حفظ وضعية اليدوي إذا الميزة معطّلة
    if (session === "manual" && !dailyShiftSettingsEnabled) {
      alert("🚫 ميزة إعدادات اليومية والشيفت اليدوي غير مفعّلة. لا يمكن حفظ الوضع اليدوي.");
      return;
    }
    setConfirmOpen(false);
    setSaving(true);
    setSuccessMsg("");
    try {
      await saveServiceAndTaxSettingsAndSessions({
        session,
        shift,
        tax,
        service,
        delivery,
        tax_is_percentage: taxIsPercentage,
        service_is_percentage: serviceIsPercentage,
        delivery_is_percentage: deliveryIsPercentage,
        is_service_required: isServiceRequired,
        is_delivery_required: isDeliveryRequired,
        is_tax_required: isTaxRequired,
        ctrl_p_print: ctrlPPrint ? 1 : 0,
        auto_preparation_print: autoPreparationPrint ? 1 : 0,
        invoice_incremant: invoiceIncrement, // أرسلها كما هي
      });
      setSuccessMsg("✅ تم حفظ البيانات بنجاح!");
      loadSettings(); // ✅ تحديث البيانات تلقائياً بعد الحفظ
    } catch (err) {
      setSuccessMsg("❌ فشل في حفظ البيانات.");
    } finally {
      setSaving(false);
      setTimeout(() => setSuccessMsg(""), 3000);
    }
  };

  const handleChangeDateClick = () => {
    // منع فتح تغيير التاريخ إذا الميزة معطّلة
    if (!dailyShiftSettingsEnabled) {
      setChangeDateError("🚫 ميزة تغيير تاريخ اليومية اليدوي غير مفعّلة.");
      return;
    }
    setChangeDateDialogOpen(true);
    setNewSessionDate(manualSessionDate || "");
    setChangeDateError("");
  };

  const handleChangeDateConfirm = () => {
    if (!dailyShiftSettingsEnabled) {
      setChangeDateError("🚫 ميزة تغيير تاريخ اليومية اليدوي غير مفعّلة.");
      return;
    }
    // افتح مربع التأكيد مباشرةً دون التحقق من حالة اليومية
    setChangeDateError("");
    setChangeDateDialogOpen(false);
    setChangeDateConfirmOpen(true);
  };

  const handleChangeDateSubmit = async () => {
    if (!dailyShiftSettingsEnabled) {
      setChangeDateError("🚫 ميزة تغيير تاريخ اليومية اليدوي غير مفعّلة.");
      return;
    }
    setChangeDateError("");
    try {
      const rootId = settings.root_id || localStorage.getItem("branch_id");
      await createManualDailySession(rootId, newSessionDate);
      setSuccessMsg("✅ تم تغيير تاريخ اليومية بنجاح!");
      setChangeDateConfirmOpen(false);
      loadSettings();
      setTimeout(() => {
        window.location.reload();
      }, 800); // ريفريش بعد نجاح التغيير
    } catch (err) {
      setChangeDateError("❌ فشل في تغيير تاريخ اليومية.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Typography color="error" align="center" sx={{ mt: 3 }}>
        تعذر جلب بيانات الجلسة والشيفت.
      </Typography>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        تعريف حالة اليويمة
      </Typography>
      {/* تحذير إذا كانت ميزة إعدادات اليومية اليدوي معطلة */}
      {!dailyShiftSettingsEnabled && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          🚫 ميزة إعدادات اليومية والشيفت اليدوي (daily_shift_settings) غير مفعّلة. خيارات الوضع اليدوي وتعريف التاريخ معطّلة.
        </Typography>
      )}
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel id="session-select-label">حالة اليومية</InputLabel>
        <Select
          labelId="session-select-label"
          value={session}
          label="حالة اليومية"
          onChange={e => setSession(e.target.value)}
        >
          <MenuItem value="manual" disabled={!dailyShiftSettingsEnabled}>يدوي</MenuItem>
          <MenuItem value="automatic">تلقائي</MenuItem>
        </Select>
      </FormControl>
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        onClick={() => setConfirmOpen(true)}
        disabled={saving || (session === "manual" && !dailyShiftSettingsEnabled)}
      >
        {saving ? "جاري الحفظ..." : "حفظ البيانات"}
      </Button>
      {successMsg && (
        <Typography sx={{ mt: 2, color: successMsg.startsWith("✅") ? "green" : "red" }}>
          {successMsg}
        </Typography>
      )}

      {session === "manual" && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            تاريخ اليومية الحالي: <b>{manualSessionDate || "غير محدد"}</b>
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleChangeDateClick}
            disabled={!dailyShiftSettingsEnabled}
            sx={{ mb: 1 }}
          >
            هل تريد تغيير تاريخ اليومية؟
          </Button>
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>تأكيد الحفظ</DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography>هل أنت متأكد أنك تريد حفظ حالة اليومية؟</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button onClick={() => setConfirmOpen(false)} color="secondary">
            إلغاء
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            تأكيد الحفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog لتغيير تاريخ اليومية */}
      <Dialog open={changeDateDialogOpen} onClose={() => setChangeDateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>تغيير تاريخ اليومية</DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography sx={{ mb: 2 }}>
            يجب قفل اليومية أولاً قبل تغيير التاريخ.
          </Typography>
          <TextField
            label="تاريخ اليومية الجديد"
            type="date"
            value={newSessionDate}
            onChange={e => setNewSessionDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          {changeDateError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {changeDateError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button onClick={() => setChangeDateDialogOpen(false)} color="secondary">
            إلغاء
          </Button>
          <Button
            onClick={handleChangeDateConfirm}
            color="primary"
            variant="contained"
            disabled={!dailyShiftSettingsEnabled}
          >
            تأكيد تغيير التاريخ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog تأكيد تغيير التاريخ بعد إغلاق اليومية */}
      <Dialog open={changeDateConfirmOpen} onClose={() => setChangeDateConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>تأكيد تغيير تاريخ اليومية</DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography>
            هل أنت متأكد أنك تريد تغيير تاريخ اليومية إلى: <b>{newSessionDate}</b>؟
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button onClick={() => setChangeDateConfirmOpen(false)} color="secondary">
            إلغاء
          </Button>
          <Button
            onClick={handleChangeDateSubmit}
            color="primary"
            variant="contained"
            disabled={!dailyShiftSettingsEnabled}
          >
            نعم، تغيير التاريخ
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SessionSettings;